import React, { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, Button, Checkbox, LinearProgress, Stack, TextField, Typography } from "@mui/material";
import billingApi, { BillingStatusOption, BillingStatusOptions, BillingWorkflowOption } from "../apis/billingApi";
import BillingNumberField from "./BillingNumberField";

interface Props {
  mspId: number;
  rates: Record<string, number>;
  completedStatuses: Record<string, string[]>;
  onRatesChange: (rates: Record<string, number>) => void;
  onStatusesChange: (statuses: Record<string, string[]>) => void;
  disabled: boolean;
}

const platformNames: Record<string, string> = { autotask: "Autotask", connectwise: "ConnectWise", halo: "Halo", superops: "SuperOps", ninjaone: "NinjaOne" };

interface RateGroup { id: string; keys: string[]; rate: number; }

function rateGroups(rates: Record<string, number>): RateGroup[] {
  const groups: RateGroup[] = [];
  for (const [key, rate] of Object.entries(rates)) {
    if (key === "*") continue;
    const group = groups.find(value => value.rate === rate);
    if (group) group.keys.push(key); else groups.push({ id: key, keys: [key], rate });
  }
  return groups;
}

function WorkflowRates({ rates, workflows, loading, error, retry, onChange, disabled }: {
  rates: Record<string, number>; workflows: BillingWorkflowOption[]; loading: boolean; error: boolean;
  retry: React.ReactNode; onChange: (rates: Record<string, number>) => void; disabled: boolean;
}) {
  const [groups, setGroups] = useState(() => rateGroups(rates));
  const currentRates = useRef(rates);
  const nextId = useRef(0);
  useEffect(() => {
    if (currentRates.current !== rates) { currentRates.current = rates; setGroups(rateGroups(rates)); }
  }, [rates]);

  const change = (nextGroups: RateGroup[], defaultRate = rates["*"]) => {
    const next: Record<string, number> = defaultRate === undefined ? {} : { "*": defaultRate };
    for (const group of nextGroups) for (const key of group.keys) next[key] = group.rate;
    currentRates.current = next;
    setGroups(nextGroups);
    onChange(next);
  };
  const label = (workflow: BillingWorkflowOption) => workflow.name + " · " + (workflow.client_name || "MSP workflow");

  return <Stack spacing={1.5}>
    <Typography variant="subtitle1">Workflow rates</Typography>
    <BillingNumberField size="small" label="Default credits per run" sx={{ width: 220, maxWidth: "100%" }} disabled={disabled}
      inputProps={{ min: 0.1, step: 0.1 }} value={rates["*"]} required={rates["*"] !== undefined}
      onChange={value => change(groups, value)} />
    <Typography variant="body2" color="text.secondary">Used for workflows without an override, including new workflows.</Typography>
    {loading && <LinearProgress aria-label="Loading workflows" />}
    {error && <Alert severity="error" action={retry}>Could not load workflows.</Alert>}
    {!loading && !error && <>
      {groups.length > 0 && <Typography variant="subtitle2">Rate overrides</Typography>}
      {groups.map((group, index) => {
        const values = group.keys.map(key => workflows.find(workflow => workflow.key === key) || { key, name: "Unavailable workflow", client_name: null });
        const assigned = new Set(groups.filter(value => value.id !== group.id).flatMap(value => value.keys));
        const options = [...workflows.filter(workflow => !assigned.has(workflow.key)), ...values.filter(value => !workflows.some(workflow => workflow.key === value.key))];
        return <Box key={group.id} sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr) auto", sm: "minmax(0, 1fr) 160px auto" }, gap: 1.5, alignItems: "start" }}>
          <Autocomplete multiple disableCloseOnSelect size="small" limitTags={2} options={options} value={values} disabled={disabled}
            sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
            getOptionLabel={label} isOptionEqualToValue={(a, b) => a.key === b.key}
            renderOption={({ key, ...props }, option, { selected }) => <li key={key} {...props}><Checkbox size="small" checked={selected} sx={{ mr: 1 }} />{label(option)}</li>}
            onChange={(_, selected) => change(groups.map(value => value.id === group.id ? { ...value, keys: selected.map(workflow => workflow.key) } : value))}
            renderInput={params => <TextField {...params} label="Workflows" inputProps={{ ...params.inputProps, "aria-label": `Workflows for override ${index + 1}` }} />} />
          <BillingNumberField size="small" label="Credits per run" sx={{ width: 160 }} disabled={disabled}
            inputProps={{ min: 0.1, step: 0.1, "aria-label": `Credits per run for override ${index + 1}` }}
            value={group.keys.length ? group.rate : group.rate || undefined} required={group.keys.length > 0}
            onChange={rate => change(groups.map(value => value.id === group.id ? { ...value, rate } : value))} />
          <Button size="small" sx={{ minHeight: 40, justifySelf: "start" }} disabled={disabled} aria-label={`Remove override ${index + 1}`}
            onClick={() => change(groups.filter(value => value.id !== group.id))}>Remove</Button>
        </Box>;
      })}
      {workflows.length > 0 && <Button size="small" sx={{ alignSelf: "flex-start" }} disabled={disabled || groups.some(group => !group.keys.length) || workflows.every(workflow => groups.some(group => group.keys.includes(workflow.key)))}
        onClick={() => setGroups([...groups, { id: `draft:${nextId.current++}`, keys: [], rate: 0 }])}>Add override</Button>}
      {!workflows.length && !groups.length && <Typography variant="body2" color="text.secondary">No workflows configured for this MSP.</Typography>}
    </>}
  </Stack>;
}

export default function BillingWorkRules({ mspId, rates, completedStatuses, onRatesChange, onStatusesChange, disabled }: Props) {
  const [workflows, setWorkflows] = useState<BillingWorkflowOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<BillingStatusOptions | null>(null);
  const [workflowError, setWorkflowError] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    setWorkflows([]); setStatusOptions(null);
    setWorkflowError(false); setStatusError(false); setWorkflowLoading(true); setStatusLoading(true);
    void billingApi.getWorkflowOptions(mspId).then(value => { if (live) setWorkflows(value.workflows); })
      .catch(() => { if (live) setWorkflowError(true); }).finally(() => { if (live) setWorkflowLoading(false); });
    void billingApi.getStatusOptions(mspId).then(value => { if (live) setStatusOptions(value); })
      .catch(() => { if (live) setStatusError(true); }).finally(() => { if (live) setStatusLoading(false); });
    return () => { live = false; };
  }, [mspId, reload]);

  const platform = statusOptions?.platform;
  const availableStatuses = statusOptions?.statuses || [];
  const selectedStatuses: BillingStatusOption[] = platform
    ? (completedStatuses[String(platform.id)] || []).map(id => availableStatuses.find(s => s.id === id) || { id, label: "Unavailable status" })
    : [];
  const statuses = [...availableStatuses, ...selectedStatuses.filter(s => !availableStatuses.some(o => o.id === s.id))];
  const retry = <Button size="small" onClick={() => setReload(value => value + 1)}>Retry</Button>;

  return <Stack spacing={2.5}>
    <WorkflowRates rates={rates} workflows={workflows} loading={workflowLoading} error={workflowError} retry={retry}
      onChange={onRatesChange} disabled={disabled} />
    <Stack spacing={1.5}>
      <Typography variant="subtitle1">Ticket completion</Typography>
      {statusLoading && <LinearProgress aria-label="Loading ticket statuses" />}
      {statusError && <Alert severity="error" action={retry}>Could not load ticket statuses.</Alert>}
      {!statusLoading && !statusError && (platform ? <Autocomplete multiple disableCloseOnSelect size="small" limitTags={3}
        sx={{ maxWidth: 560 }} options={statuses} value={selectedStatuses} disabled={disabled}
        getOptionLabel={option => option.label} isOptionEqualToValue={(a, b) => a.id === b.id}
        renderOption={({ key, ...props }, option, { selected: checked }) => <li key={key} {...props}><Checkbox size="small" checked={checked} sx={{ mr: 1 }} />{option.label}</li>}
        onChange={(_, value) => onStatusesChange({ ...completedStatuses, [String(platform.id)]: value.map(s => s.id) })}
        renderInput={params => <TextField {...params} label={(platformNames[platform.name.toLowerCase()] || platform.name) + " completed statuses"}
          helperText="Tickets are charged when they reach a selected status." />} />
        : <Typography variant="body2" color="text.secondary">Connect a PSA to choose completion statuses.</Typography>)}
    </Stack>
  </Stack>;
}
